import React, { FC, useEffect, useState, useCallback } from 'react'
import { Form, Formik, FormikProps } from 'formik'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { showErrorToast, showSuccessToast } from '../../../../shared/components/ToastUtils'
import { ConfirmModal } from '../../../../shared/components/ConfirmModal'
import { IUserModel, initialUser, validationUser, validationUserEdit } from './UserModel'
import { createUser, updateUser, deleteUser, loadUsers, loadUser, loadUserRoles } from './Api'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import UserTable from './UserTable'
import { IRoleModel } from '../role/RoleModel'

const UserPage: FC = () => {
  const [tab, setTab] = useState<'form' | 'list'>('form')
  const [users, setUsers] = useState<IUserModel[]>([])
  const [roles, setRoles] = useState<IRoleModel[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [resetUser, setResetUser] = useState<IUserModel | null>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)
  const [selectedUser, setSelectedUser] = useState<IUserModel>(initialUser)

  const isEditMode = !!selectedId

  const fetchUsers = useCallback(async (page = 0) => {
    setLoading(true)
    try {
      const res = await loadUsers(page)
      if (res.data.hasError) throw new Error(res.data.message)
      setUsers(res.data.payload.content)
      setTotalPages(res.data.payload.totalPages)
    } catch {
      showErrorToast('Kullanıcılar yüklenemedi')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchRoles = useCallback(async () => {
    try {
      const res = await loadUserRoles()
      if (!res.data.hasError) setRoles(res.data.payload.content)
    } catch {
      // roles boş kalır
    }
  }, [])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  useEffect(() => {
    if (tab === 'list') fetchUsers(0)
  }, [tab, fetchUsers])

  const handleSave = async (values: IUserModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateUser({ ...values, id: selectedId })
        showSuccessToast('Kullanıcı başarıyla güncellendi')
      } else {
        await createUser(values)
        showSuccessToast('Kullanıcı başarıyla oluşturuldu')
      }
      resetForm()
      setSelectedId(null)
      setSelectedUser(initialUser)
      setTab('list')
      fetchUsers(0)
    } catch {
      showErrorToast('İşlem sırasında hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = async (user: IUserModel) => {
    const userId = user.id ?? null
    if (!userId) {
      showErrorToast('Kullanıcı ID bilgisi bulunamadı')
      return
    }
    setSelectedId(userId)
    try {
      const res = await loadUser(userId)
      if (res.data.hasError) {
        showErrorToast('Kullanıcı bilgileri yüklenemedi')
      } else {
        const payload = res.data.payload
        const roleFromPayload = (payload as IUserModel & {role?: {id?: string | null}}).role?.id
        setSelectedUser({
          ...initialUser,
          ...payload,
          roleId: payload.roleId || roleFromPayload || '',
          password: '',
          passwordConfirm: '',
        })
      }
    } catch {
      showErrorToast('Kullanıcı bilgileri yüklenemedi')
    }
    setTab('form')
  }

  const handleDeleteClick = (user: IUserModel) => {
    setDeleteId(user.id ?? null)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteId) return
    setLoading(true)
    try {
      await deleteUser(deleteId)
      showSuccessToast('Kullanıcı silindi')
      setShowDeleteModal(false)
      setDeleteId(null)
      fetchUsers(currentPage)
    } catch {
      showErrorToast('Silme işlemi başarısız')
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = (user: IUserModel) => {
    setResetUser(user)
    setShowResetModal(true)
  }

  const handleResetPasswordConfirm = async () => {
    // Backend entegrasyonu için hazır
    showSuccessToast(`${resetUser?.email} adresine şifre sıfırlama maili gönderildi`)
    setShowResetModal(false)
    setResetUser(null)
  }

  const tabOptions = [
    { key: 'form' as const, title: isEditMode ? 'Kullanıcı Düzenle' : 'Kullanıcı Oluştur', icon: 'bi bi-person-plus' },
    { key: 'list' as const, title: 'Kullanıcı Listesi', icon: 'bi bi-people' },
  ]

  return (
    <div className='card'>
      <ToastContainer />

      {/* Sil modal */}
      <ConfirmModal
        show={showDeleteModal}
        header='Kullanıcı Sil'
        text='Bu kullanıcıyı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.'
        handleClose={() => setShowDeleteModal(false)}
        applyOnClick={handleDeleteConfirm}
      />

      {/* Şifre sıfırla modal */}
      <ConfirmModal
        show={showResetModal}
        header='Şifre Sıfırla'
        text={`${resetUser?.email} kullanıcısına şifre sıfırlama maili gönderilsin mi?`}
        handleClose={() => setShowResetModal(false)}
        applyOnClick={handleResetPasswordConfirm}
      />

      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <h3 className='card-label fw-bold fs-3'>
            <i className='bi bi-person-gear me-2 text-primary' />
            Kullanıcı Yönetimi
          </h3>
        </div>
      </div>

      <div className='card-body pt-0'>
        {/* Tabs */}
        <div className='d-flex flex-wrap gap-1 mb-6'>
          {tabOptions.map((t) => (
            <button
              key={t.key}
              type='button'
              className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-light'}`}
              onClick={() => {
                if (t.key === 'form' && tab === 'list') {
                  setSelectedId(null)
                  setSelectedUser(initialUser)
                }
                setTab(t.key)
              }}
            >
              <i className={`${t.icon} me-2`} />
              {t.title}
            </button>
          ))}
        </div>

        {/* FORM TAB */}
        {tab === 'form' && (
          <Formik
            enableReinitialize
            initialValues={selectedUser}
            validationSchema={isEditMode ? validationUserEdit : validationUser}
            onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
          >
            {(formik: FormikProps<IUserModel>) => (
              <Form>
                <div className='row g-6'>

                  {/* Sol kolon */}
                  <div className='col-xl-6'>
                    <div className='card border h-100'>
                      <div className='card-header min-h-50px'>
                        <h5 className='card-title fs-6 fw-bold'>
                          <i className='bi bi-person me-2 text-primary' />
                          Kişisel Bilgiler
                        </h5>
                      </div>
                      <div className='card-body p-6'>
                        <div className='row g-4'>
                          <div className='col-sm-6'>
                            <TextInput name='firstName' label='Ad' placeholder='Ad' required />
                          </div>
                          <div className='col-sm-6'>
                            <TextInput name='lastName' label='Soyad' placeholder='Soyad' required />
                          </div>
                          <div className='col-sm-6'>
                            <TextInput name='email' label='E-mail' placeholder='ornek@email.com' required />
                          </div>
                          <div className='col-sm-6'>
                            <TextInput name='phone' label='Telefon' placeholder='+90 5XX XXX XX XX' />
                          </div>
                          <div className='col-sm-6'>
                            <TextInput name='username' label='Kullanıcı Adı' placeholder='kullanici.adi' required />
                          </div>
                          <div className='col-sm-6'>
                            <SelectInput
                              name='roleId'
                              label='Rol'
                              className='form-select form-select-solid'
                              firstItemLabel='Rol seçiniz'
                              options={roles.map((r) => ({ value: r.id ?? '', label: r.roleName }))}
                            />
                          </div>
                        </div>

                        {/* Durum toggleları */}
                        <div className='separator my-4' />
                        <div className='row g-4'>
                          <div className='col-sm-6'>
                            <div className='d-flex align-items-center gap-3'>
                              <div className='form-check form-switch'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  id='activeSwitch'
                                  checked={formik.values.active}
                                  onChange={(e) => formik.setFieldValue('active', e.target.checked)}
                                />
                              </div>
                              <label className='form-label mb-0 fw-bold' htmlFor='activeSwitch'>
                                {formik.values.active ? (
                                  <span className='badge badge-light-success'>Aktif</span>
                                ) : (
                                  <span className='badge badge-light-danger'>Pasif</span>
                                )}
                              </label>
                            </div>
                          </div>
                          <div className='col-sm-6'>
                            <div className='d-flex align-items-center gap-3'>
                              <div className='form-check form-switch'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  id='mustChangePasswordSwitch'
                                  checked={formik.values.mustChangePassword}
                                  onChange={(e) =>
                                    formik.setFieldValue('mustChangePassword', e.target.checked)
                                  }
                                />
                              </div>
                              <label className='form-label mb-0' htmlFor='mustChangePasswordSwitch'>
                                <span className='text-muted fs-7'>İlk girişte şifre değiştirsin</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sağ kolon — Şifre */}
                  <div className='col-xl-6'>
                    <div className='card border h-100'>
                      <div className='card-header min-h-50px'>
                        <h5 className='card-title fs-6 fw-bold'>
                          <i className='bi bi-shield-lock me-2 text-warning' />
                          {isEditMode ? 'Şifre Güncelle' : 'Şifre Belirle'}
                        </h5>
                      </div>
                      <div className='card-body p-6'>
                        {isEditMode && (
                          <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-4 mb-5'>
                            <i className='bi bi-info-circle-fill text-warning fs-4 me-3 mt-1' />
                            <div className='text-muted fs-7'>
                              Şifreyi değiştirmek istemiyorsanız bu alanları boş bırakınız.
                              Şifre sıfırlamak için tablodaki{' '}
                              <strong>"Şifre Sıfırla"</strong> butonunu kullanabilirsiniz.
                            </div>
                          </div>
                        )}

                        {/* Şifre kuvveti göstergesi */}
                        {formik.values.password && (
                          <PasswordStrength password={formik.values.password} />
                        )}

                        {/* Şifre */}
                        <div className='mb-4'>
                          <label className={`form-label fw-bold ${!isEditMode ? 'required' : ''}`}>
                            Şifre
                          </label>
                          <div className='input-group'>
                            <input
                              type={showPassword ? 'text' : 'password'}
                              className={`form-control form-control-solid ${
                                formik.touched.password && formik.errors.password ? 'is-invalid' : ''
                              }`}
                              placeholder='En az 8 karakter'
                              {...formik.getFieldProps('password')}
                            />
                            <button
                              type='button'
                              className='btn btn-outline-secondary'
                              onClick={() => setShowPassword((v) => !v)}
                              tabIndex={-1}
                            >
                              <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                            </button>
                            {formik.touched.password && formik.errors.password && (
                              <div className='invalid-feedback'>{formik.errors.password}</div>
                            )}
                          </div>
                        </div>

                        {/* Şifre tekrar */}
                        <div className='mb-4'>
                          <label className={`form-label fw-bold ${!isEditMode ? 'required' : ''}`}>
                            Şifre Tekrarı
                          </label>
                          <div className='input-group'>
                            <input
                              type={showPasswordConfirm ? 'text' : 'password'}
                              className={`form-control form-control-solid ${
                                formik.touched.passwordConfirm && formik.errors.passwordConfirm
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              placeholder='Şifreyi tekrar giriniz'
                              {...formik.getFieldProps('passwordConfirm')}
                            />
                            <button
                              type='button'
                              className='btn btn-outline-secondary'
                              onClick={() => setShowPasswordConfirm((v) => !v)}
                              tabIndex={-1}
                            >
                              <i className={`bi ${showPasswordConfirm ? 'bi-eye-slash' : 'bi-eye'}`} />
                            </button>
                            {formik.touched.passwordConfirm && formik.errors.passwordConfirm && (
                              <div className='invalid-feedback'>{formik.errors.passwordConfirm}</div>
                            )}
                          </div>
                        </div>

                        {/* Şifre kuralları */}
                        <div className='separator my-4' />
                        <div className='fs-7 text-muted'>
                          <div className='fw-bold mb-2'>Şifre Kuralları:</div>
                          <PasswordRule met={formik.values.password.length >= 8} text='En az 8 karakter' />
                          <PasswordRule met={/[A-Z]/.test(formik.values.password)} text='En az bir büyük harf' />
                          <PasswordRule met={/[0-9]/.test(formik.values.password)} text='En az bir rakam' />
                          <PasswordRule
                            met={
                              formik.values.passwordConfirm !== '' &&
                              formik.values.password === formik.values.passwordConfirm
                            }
                            text='Şifreler eşleşiyor'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Butonlar */}
                <div className='d-flex gap-3 mt-6 justify-content-end'>
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={() => {
                      formik.resetForm()
                      setSelectedId(null)
                      setSelectedUser(initialUser)
                    }}
                  >
                    <i className='bi bi-arrow-counterclockwise me-2' />
                    Temizle
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary px-10'
                    disabled={loading || formik.isSubmitting}
                  >
                    {loading ? (
                      <span className='spinner-border spinner-border-sm me-2' />
                    ) : (
                      <i className='bi bi-check-lg me-2' />
                    )}
                    {isEditMode ? 'Güncelle' : 'Kullanıcı Oluştur'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}

        {/* LIST TAB */}
        {tab === 'list' && (
          <UserTable
            users={users}
            currentPage={currentPage}
            totalPages={totalPages}
            loading={loading}
            onPageChange={(page) => {
              setCurrentPage(page)
              fetchUsers(page)
            }}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            onResetPassword={handleResetPassword}
          />
        )}
      </div>
    </div>
  )
}

// Şifre kuvveti bileşeni
const PasswordStrength: FC<{ password: string }> = ({ password }) => {
  const getStrength = () => {
    let score = 0
    if (password.length >= 8) score++
    if (password.length >= 12) score++
    if (/[A-Z]/.test(password)) score++
    if (/[0-9]/.test(password)) score++
    if (/[^A-Za-z0-9]/.test(password)) score++
    return score
  }

  const strength = getStrength()
  const labels = ['', 'Çok Zayıf', 'Zayıf', 'Orta', 'Güçlü', 'Çok Güçlü']
  const colors = ['', 'danger', 'warning', 'info', 'primary', 'success']

  return (
    <div className='mb-4'>
      <div className='d-flex justify-content-between mb-1'>
        <span className='fs-8 text-muted'>Şifre Kuvveti</span>
        <span className={`fs-8 fw-bold text-${colors[strength]}`}>{labels[strength]}</span>
      </div>
      <div className='d-flex gap-1'>
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`flex-fill rounded`}
            style={{
              height: '4px',
              backgroundColor: i <= strength ? `var(--bs-${colors[strength]})` : '#e4e6ef',
              transition: 'background-color 0.3s',
            }}
          />
        ))}
      </div>
    </div>
  )
}

// Şifre kural satırı
const PasswordRule: FC<{ met: boolean; text: string }> = ({ met, text }) => (
  <div className={`d-flex align-items-center gap-2 mb-1 ${met ? 'text-success' : 'text-muted'}`}>
    <i className={`bi ${met ? 'bi-check-circle-fill' : 'bi-circle'} fs-7`} />
    <span>{text}</span>
  </div>
)

export { UserPage }
